/**
 * Connecting a channel to Twitch, and its owner's view of that link.
 *
 * Connecting is a full-page trip through Twitch's consent screen, so `start`
 * returns where to send the browser rather than doing anything itself. The
 * server brings the person back to the channel, signed in as its owner.
 */
import {
  ChannelModerator, ChannelModeratorList, LoginStarted, TwitchConnection,
  type TwitchConnectionPatch,
} from '@tomo/contracts';
import { request } from './api.js';

export const twitchConnection = {
  /**
   * The Twitch consent URL for connecting the channel you broadcast.
   *
   * `returnTo` is where a RECONNECT comes back to. A first connect leaves it
   * out and lands on the channel it just made, whose id nobody knows yet.
   */
  async start(returnTo?: string): Promise<string> {
    const started = await request('/v1/auth/twitch/connect', LoginStarted, {
      method: 'POST',
      body: returnTo ? { return_to: returnTo } : {},
    });
    return started.authorize_url;
  },

  get: (channelId: string, signal?: AbortSignal) =>
    request(`/v1/channels/${channelId}/twitch`, TwitchConnection, signal ? { signal } : {}),

  patch: (channelId: string, body: TwitchConnectionPatch) =>
    request(`/v1/channels/${channelId}/twitch`, TwitchConnection, { method: 'PATCH', body }),

  moderators: async (channelId: string, signal?: AbortSignal) =>
    (await request(`/v1/channels/${channelId}/moderators`, ChannelModeratorList, signal ? { signal } : {})).data,

  suspend: (channelId: string, uid: string, suspended: boolean) =>
    request(`/v1/channels/${channelId}/moderators/${encodeURIComponent(uid)}`, ChannelModerator, {
      method: 'PATCH', body: { suspended },
    }),
};
